import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { XCircle, Loader2, AlertCircle, Calendar, Clock, User, Stethoscope, ChevronLeft } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to format time to AM/PM
const formatTimeToAMPM = (time24) => {
  if (!time24 || time24 === 'queue') return time24;
  
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

// Cancel reason options
const CANCEL_REASONS = [
  { value: 'cant_make_it', label: "Can't make it" },
  { value: 'found_another', label: 'Found another clinic' },
  { value: 'feeling_better', label: 'Feeling better' },
  { value: 'financial', label: 'Financial reasons' },
  { value: 'other', label: 'Other' }
];

const PatientCancelPage = () => {
  const { appointmentId, token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [alreadyActioned, setAlreadyActioned] = useState(false);
  const [actionedStatus, setActionedStatus] = useState('');
  const [appointmentDetails, setAppointmentDetails] = useState(null);
  const [clinicName, setClinicName] = useState('');
  
  // Form states
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNotes, setCancelNotes] = useState('');

  useEffect(() => {
    const fetchAppointment = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/patient/appointment/${appointmentId}/${token}`);
        
        if (res.data.already_actioned) {
          setAlreadyActioned(true);
          setActionedStatus(res.data.status);
        } else {
          setAppointmentDetails(res.data.appointment);
          setClinicName(res.data.clinic_name);
        }
      } catch (err) {
        console.error('Error fetching appointment:', err);
        setError(err.response?.data?.detail || 'Invalid or expired link. Please contact the clinic.');
      } finally {
        setLoading(false);
      }
    };

    fetchAppointment();
  }, [appointmentId, token]);

  const handleSubmit = async () => {
    if (!cancelReason) {
      setError('Please select a reason for cancellation');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const res = await axios.post(`${API_URL}/api/patient/cancel/${appointmentId}/${token}`, {
        reason_category: cancelReason,
        notes: cancelNotes
      });

      if (res.data.success) {
        setSuccess(true);
      } else {
        setError(res.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to cancel appointment');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-red-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state (initial load error)
  if (error && !appointmentDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Oops!</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already actioned state
  if (alreadyActioned) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Already {actionedStatus}</h2>
            <p className="text-gray-600">This appointment has already been {actionedStatus}.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-gray-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Appointment Cancelled</h2>
            <p className="text-gray-600 mb-6">Your appointment has been cancelled successfully.</p>
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                We hope to see you again soon. You can close this window now.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Cancel form
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 p-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{clinicName}</h1>
          <p className="text-gray-600">Cancel Appointment</p>
        </div>

        {/* Appointment Details */}
        {appointmentDetails && (
          <Card className="mb-6">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center gap-2 text-gray-700">
                <User className="w-4 h-4 text-gray-400" />
                <span className="font-medium">{appointmentDetails.patient_name}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <Stethoscope className="w-4 h-4 text-gray-400" />
                <span>{appointmentDetails.service_name}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span>{new Date(appointmentDetails.scheduled_date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
              </div>
              {appointmentDetails.scheduled_time && (
                <div className="flex items-center gap-2 text-gray-700">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span>{formatTimeToAMPM(appointmentDetails.scheduled_time)}</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Cancel Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Why are you cancelling?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Reason Selection */}
            <div className="space-y-2">
              {CANCEL_REASONS.map((reason) => (
                <button
                  key={reason.value}
                  onClick={() => {
                    setCancelReason(reason.value);
                    setError(null);
                  }}
                  className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
                    cancelReason === reason.value
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {reason.label}
                </button>
              ))}
            </div>

            {/* Additional Notes */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Additional notes (optional)
              </label>
              <textarea
                value={cancelNotes}
                onChange={(e) => setCancelNotes(e.target.value)}
                placeholder="Any additional information you'd like to share..."
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                rows={3}
              />
            </div>

            <Button
              onClick={handleSubmit}
              disabled={submitting || !cancelReason}
              className="w-full h-12 bg-red-600 hover:bg-red-700 text-lg"
              data-testid="submit-cancel-btn"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : null}
              Confirm Cancellation
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PatientCancelPage;
